<template>
    <!-- SCHEDULE THERAPY BLOCK -->
    <div class="card border-0 rounded-0 bg-white mb-25" v-if="update_enabled">
        <div class="card-body p-15 p-sm-20 p-sm-25 pt-lg-30 pb-lg-30 ps-lg-25 pe-lg-25 letter-spacing">
            <ScheduleTherapy v-if="schedule_therapy === true" :patient="patient" @scheduled="therapyScheduled($event)"
                @discard="discardTherapySchedule" />
            <!-- ADD THERAPY -->
            <div v-if="!schedule_therapy" class="d-flex align-items-end justify-content-start flex-1 ms-auto">
                <button class="default-btn transition fw-medium fs-md-15 fs-lg-16" v-on:click="provideTherapy">
                    {{ $t('feature.therapy.provide_therapy') }}
                </button>
            </div>
        </div>
    </div>
    <!-- THERAPIES LIST -->
    <FKAccordion id="therapies" :title="$t('feature.therapy.name')" class="mb-25">
        <SpinnerLoading v-if="all_therapies === undefined" class="mt-50" />
        <template v-else-if="all_therapies.length === 0">
            <p>{{ $t('feature.therapy.no_therapy_scheduled') }}</p>
        </template>
        <!-- ACTIVE THERAPIES -->
        <div class="d-flex justify-content-between gap-3 flex-wrap">
            <h6 class="fw-medium text-body-tertiary mb-10" v-if="active_therapies && active_therapies.length !== 0">{{
                $tc('feature.therapy.active_therapies', active_therapies.length) }}</h6>
            <!-- SHOW PRN -->
            <div class="form-check form-switch d-inline-block d-flex gap-2 ms-auto mb-10"
                v-if="prn_enabled && all_therapies?.length !== 0">
                <input class="form-check-input shadow-none" type="checkbox" id="show_prn" role="switch"
                    v-model="show_prn_therapies">
                <label class="form-check-label fw-medium position-relative" for="show_prn">
                    {{ $t('feature.therapy.show_prn') }}
                </label>
            </div>
        </div>
        <div v-if="active_therapies?.length !== 0" class="mb-25">
            <TherapyList :therapies="active_therapies ?? []" :patient="patient" :show_stats="true" :show_actions="true"
                @stop="stopTherapy($event)" :show_overall_trend="true" />
        </div>
        <!-- DAILY CALENDAR -->
        <FKAccordion id="therapies_calendar" class="mb-25" :title="$t('feature.therapy.daily_calendar')">
            <Transition name="fade">
                <template v-if="all_therapies !== undefined">
                    <TherapyIntakeCalendar :therapies="all_therapies ?? []" :patient="patient" />
                </template>
            </Transition>
        </FKAccordion>
        <!-- INACTIVE THERAPIES -->
        <FKAccordion id="inactive-therapies" :title="$tc('feature.therapy.inactive_therapies', inactive_therapies?.length ?? 0)"
            v-if="inactive_therapies && inactive_therapies?.length !== 0">
            <TherapyList :therapies="inactive_therapies ?? []" :patient="patient" :show_stats="true"
                :show_actions="true" @delete="deleteTherapy($event)" />
        </FKAccordion>
    </FKAccordion>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import TherapyList from "@/components/FK/PatientDetail/TherapyList.vue";
import TherapyIntakeCalendar from "@/components/FK/PatientDetail/TherapyIntakeCalendar.vue";
import ScheduleTherapy, { ScheduleTherapies } from "@/components/FK/PatientDetail/ScheduleTherapy.vue";
import { PatientInfo } from "@/utils/models/user.model";
import { Therapy, Intake, TherapyStats } from "@/utils/models/therapy.model";
import API from "@/services/api/api.service";
import FKAccordion from "@/components/FK/FKAccordion.vue";
import { is_active_therapy } from "@/utils/methods/therapy_dates";
import config from "@/utils/const/config";
import { bool_to_int } from "@/utils/methods/int_to_bool";
import stateStore from "@/utils/store";

export default defineComponent({
    name: "TherapyView",
    data: function () {
        return {
            all_therapies: undefined,
            intakes: undefined,
            therapyStats: undefined,
            dayStats: undefined,
            schedule_therapy: false,
            date: new Date(Date.now()),
            show_prn_therapies: config.feature.show_prn_therapies
        } as {
            all_therapies?: Therapy[] | undefined,
            intakes?: Intake[],
            therapyStats?: TherapyStats,
            dayStats?: TherapyStats,
            schedule_therapy: boolean,
            date: Date,
            show_prn_therapies: boolean
        };
    },
    computed: {
        patient() { return this.$route.meta.patient as PatientInfo },
        update_enabled(): boolean { return stateStore.getCenterPermission((this.$route.meta.patient as PatientInfo).center_id, 'update') },
        prn_enabled(): boolean {
            return config.feature.prn_therapies;
        },
        visible_therapies(): Therapy[] | undefined {
            return (this.show_prn_therapies === true ? this.all_therapies : this.all_therapies?.filter((therapy) => therapy.is_prn === false))?.sort((a, b) => bool_to_int(a.is_prn) - bool_to_int(b.is_prn));
        },
        active_therapies(): Therapy[] | undefined {
            return this.visible_therapies?.filter((item) => is_active_therapy(item));
        },
        inactive_therapies(): Therapy[] | undefined {
            return this.visible_therapies?.filter((item) => !is_active_therapy(item));
        }
    },
    mounted: function () {
        this.loadTherapies();
    },
    methods: {
        therapyScheduled(data: ScheduleTherapies) {
            if (data.failed === false) {
                this.discardTherapySchedule();
            }
            this.all_therapies ??= [];
            this.all_therapies = [...this.all_therapies, ...data.success];
        },
        loadTherapies() {
            this.all_therapies = undefined;
            API.getTherapies(this.patient.id!).then((response) => {
                if (response.data.data !== null && response.data.data.length !== 0) {
                    this.all_therapies = response.data.data;
                } else {
                    this.all_therapies = [];
                }
            });
        },
        provideTherapy() {
            this.schedule_therapy = true;
        },
        discardTherapySchedule() {
            this.schedule_therapy = false;
        },
        deleteTherapy(therapy: Therapy) {
            this.all_therapies = this.all_therapies?.filter((t) => t.id !== therapy.id);
        },
        stopTherapy(therapy: Therapy) {
            let index = (this.all_therapies ?? []).findIndex((item) => item.id === therapy.id);
            if (index >= 0) {
                this.all_therapies![index] = therapy;
            }
        }
    },
    components: {
        SpinnerLoading,
        ScheduleTherapy,
        TherapyIntakeCalendar,
        TherapyList,
        FKAccordion
    }
});
</script>